import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

// amplify
import { Auth, API } from 'aws-amplify'
import { listOpportunityTemplatesByOrganization, getOpportunity } from '../../graphql/queries'
import { createOpportunityTemplate } from '../../graphql/mutations'

// models
import { Cause, Skill, OpportunityTemplate } from '../../../API'

import { setLoading } from './loading'

interface TemplatesState {
    organizationOpportunityTemplates: OpportunityTemplate[] | null,
    flourishOpportunityTemplates: OpportunityTemplate[] | null
}

const initialState: TemplatesState = {
    organizationOpportunityTemplates: null,
    flourishOpportunityTemplates: null
}

export const getOrganizationOpportunityTemplates: any = createAsyncThunk(
    'templates/getOrganizationOpportunityTemplates',
    async (props: any, { dispatch, getState }) => {
        try {

            const { authAccount, templates }: any = getState()

            if (authAccount.selectedStaffProfile && authAccount.selectedStaffProfile.organizationID && templates.organizationOpportunityTemplates == null) {
                const { data }: any = await API.graphql({
                    query: listOpportunityTemplatesByOrganization,
                    variables: {
                        organizationID: authAccount.selectedStaffProfile.organizationID
                    }
                })

                console.log('pulled templates: ', data)

                if (data && data.listOpportunityTemplatesByOrganization && data.listOpportunityTemplatesByOrganization.items) {
                    return {
                        opportunityTemplates: data.listOpportunityTemplatesByOrganization.items
                    }
                }
            }


        } catch (err) {
            console.error(err)
        }
    }
)

export const getFlourishOpportunityTemplates: any = createAsyncThunk(
    'templates/getFlourishOpportunityTemplates',
    async (props: any, { dispatch, getState }) => {
        try {

            const { authAccount, templates }: any = getState()

            if (authAccount.selectedStaffProfile && authAccount.selectedStaffProfile.organizationID && templates.flourishOpportunityTemplates == null) {
                const { data }: any = await API.graphql({
                    query: listOpportunityTemplatesByOrganization,
                    variables: {
                        organizationID: '00000000-0000-0000-0000-000000000000'
                    }
                })

                console.log('pulled templates: ', data)

                if (data && data.listOpportunityTemplatesByOrganization && data.listOpportunityTemplatesByOrganization.items) {
                    return {
                        opportunityTemplates: data.listOpportunityTemplatesByOrganization.items
                    }
                }
            }


        } catch (err) {
            console.error(err)
        }
    }
)

export const createOrganizationOpportunityTemplate: any = createAsyncThunk(
    'templates/createOrganizationOpportunityTemplate',
    async (props: { name: string, description?: string }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({ loading: true, message: 'Creating template. This could take a few moments...' }))

            const { authAccount, opportunities }: any = getState()

            if (authAccount.selectedStaffProfile && authAccount.selectedStaffProfile.organizationID && opportunities && opportunities.selectedOpportunity) {

                const getCurrentOpportunityDataResponse: any = await API.graphql({
                    query: `
                    query GetOpportunity($id: ID!) {
                        getOpportunity(id: $id) {
                          id
                          organizationID
                          programID
                          program {
                            id
                            organizationID
                            name
                            description
                            theWhy
                            leadID
                            stringifiedCauses
                            archived
                            createdAt
                            updatedAt
                          }
                          opportunityLeadID
                          type
                          status
                          headerImage
                          title
                          descriptionRTE
                          theWhy
                          expectedImpact
                          whatWillHappen
                          volunteerGain
                          roles {
                            items {
                              id
                              roleTitle
                              live
                              roleCoordinatorID
                              recurrence
                              weeklyRecurrenceDays
                              weeklyRecurrenceSameVolunteers
                              volunteersPerShift
                              differentNumberOfVolunteersPerShift
                              volunteerCommitmentMonths
                              volunteerCommitmentCanRenew
                              roleRequirements
                              ageRequirement
                              minorsRequireSupervision
                              stringifiedSkills
                              stringifiedSkillsJSON
                              stringifiedCertifications
                              shifts {
                                nextToken
                                items {
                                    id
                                    type
                                    live
                                    name
                                    summary
                                    startDate
                                    startTime
                                    endDate
                                    endTime
                                    timezone
                                    timeFrame
                                    timeFrameString
                                    recurrence
                                    weeklyRecurringDay
                                    monthlyRecurrenceWeek
                                    monthlyRecurrenceDay
                                    flexibleHours
                                    flexibleTimePeriod
                                    volunteersNeeded
                                    volunteerCoordinatorID
                                    roleID
                                    roleStringifiedSkills
                                    roleStringifiedCauses
                                    locationID
                                    location {
                                      id
                                      type
                                      name
                                      details
                                      addressID
                                      address {
                                        id
                                        streetAddress
                                        unit
                                        city
                                        state
                                        zip
                                        zipPlusFour
                                        longitude
                                        latitude
                                        createdAt
                                        updatedAt
                                      }
                                      organizationID
                                      createdAt
                                      updatedAt
                                    }
                                    locationLongitude
                                    locationLatitude
                                    locationType
                                    locationZipEstimate
                                    locationVariousDescription
                                    createdAt
                                    updatedAt
                                    volunteerCoordinator {
                                      id
                                      authAccountID
                                      organizationID
                                      role
                                      name
                                      createdAt
                                      updatedAt
                                      authAccount {
                                        id
                                        volunteerProfileID
                                        volunteerProfile {
                                          id
                                          authAccountID
                                          email
                                          firstName
                                          lastName
                                          gender
                                          dateOfBirth
                                          streetAddress
                                          city
                                          state
                                          zip
                                          website
                                          additionalInformation
                                          availabilityStringified
                                          latitude
                                          longitude
                                          createdAt
                                          updatedAt
                                        }
                                        lastSelectedStaffProfileID
                                        communicationsProfileID
                                        communicationsProfile {
                                          id
                                          authAccountID
                                          streamJwt
                                          notificationSettingsID
                                          createdAt
                                          updatedAt
                                        }
                                        name
                                        firstName
                                        lastName
                                        primaryEmail
                                        phoneNumber
                                        dateOfBirth
                                        gender
                                        selfDescribedGender
                                        addressID
                                        address {
                                          id
                                          streetAddress
                                          unit
                                          city
                                          state
                                          zip
                                          zipPlusFour
                                          longitude
                                          latitude
                                          createdAt
                                          updatedAt
                                        }
                                        website
                                        resumeURI
                                        bio
                                        profilePictureURI
                                        createdAt
                                        updatedAt
                                        cognitoAccounts {
                                          nextToken
                                        }
                                        owner
                                        staffProfile {
                                          nextToken
                                        }
                                        lastSelectedStaffProfile {
                                          id
                                          authAccountID
                                          organizationID
                                          role
                                          name
                                          createdAt
                                          updatedAt
                                          owner
                                        }
                                      }
                                      owner
                                    }
                                }
                              }
                              createdAt
                              updatedAt
                              roleCoordinator {
                                id
                                authAccountID
                                organizationID
                                role
                                name
                                createdAt
                                updatedAt
                                owner
                              }
                              organization {
                                id
                                name
                                shortName
                                description
                                locationID
                                website
                                phoneNumber
                                stripeConnectedAccountID
                                stripeConnectedAccountStatus
                                headerImageURI
                                createdAt
                                updatedAt
                                owner
                              }
                            }
                            nextToken
                          }
                          stringifiedCauses
                          stringifiedCausesJSON
                          application {
                            items {
                              id
                              opportunityID
                              opportunity {
                                id
                                organizationID
                                programID
                                opportunityLeadID
                                type
                                status
                                headerImage
                                title
                                descriptionRTE
                                theWhy
                                expectedImpact
                                whatWillHappen
                                volunteerGain
                                stringifiedCauses
                                stringifiedCausesJSON
                                createdAt
                                updatedAt
                              }
                              roleID
                              role {
                                id
                                opportunityID
                                organizationID
                                roleTitle
                                live
                                roleCoordinatorID
                                recurrence
                                weeklyRecurrenceDays
                                weeklyRecurrenceSameVolunteers
                                volunteersPerShift
                                differentNumberOfVolunteersPerShift
                                volunteerCommitmentMonths
                                volunteerCommitmentCanRenew
                                roleRequirements
                                ageRequirement
                                minorsRequireSupervision
                                stringifiedSkills
                                stringifiedSkillsJSON
                                stringifiedCertifications
                                createdAt
                                updatedAt
                              }
                              screeningCallRequired
                              signUpOnly
                              requireScreeningCall
                              requireBackgroundCheck
                              recruitmentDeadlineForVolunteers
                              targetNumberOfVolunteerHours
                              minimumNumberOfVolunteersRequired
                              customApplicationQuestions {
                                nextToken
                              }
                              createdAt
                              updatedAt
                            }
                            nextToken
                          }
                          createdAt
                          updatedAt
                          opportunityLead {
                            id
                            authAccountID
                            organizationID
                            role
                            name
                            createdAt
                            updatedAt
                            authAccount {
                              id
                              volunteerProfileID
                              volunteerProfile {
                                id
                                authAccountID
                                email
                                firstName
                                lastName
                                gender
                                dateOfBirth
                                streetAddress
                                city
                                state
                                zip
                                website
                                additionalInformation
                                availabilityStringified
                                latitude
                                longitude
                                createdAt
                                updatedAt
                              }
                              lastSelectedStaffProfileID
                              communicationsProfileID
                              communicationsProfile {
                                id
                                authAccountID
                                streamJwt
                                notificationSettingsID
                                createdAt
                                updatedAt
                              }
                              name
                              firstName
                              lastName
                              primaryEmail
                              phoneNumber
                              dateOfBirth
                              gender
                              selfDescribedGender
                              addressID
                              address {
                                id
                                streetAddress
                                unit
                                city
                                state
                                zip
                                zipPlusFour
                                longitude
                                latitude
                                createdAt
                                updatedAt
                              }
                              website
                              resumeURI
                              bio
                              profilePictureURI
                              createdAt
                              updatedAt
                              cognitoAccounts {
                                nextToken
                              }
                              owner
                              staffProfile {
                                nextToken
                              }
                              lastSelectedStaffProfile {
                                id
                                authAccountID
                                organizationID
                                role
                                name
                                createdAt
                                updatedAt
                                owner
                              }
                            }
                            owner
                            organization {
                              id
                              name
                              shortName
                              description
                              locationID
                              location {
                                id
                                type
                                name
                                details
                                addressID
                                organizationID
                                createdAt
                                updatedAt
                              }
                              website
                              phoneNumber
                              stripeConnectedAccountID
                              stripeConnectedAccountStatus
                              headerImageURI
                              createdAt
                              updatedAt
                              owner
                            }
                          }
                          organization {
                            id
                            name
                            shortName
                            description
                            locationID
                            location {
                              id
                              type
                              name
                              details
                              addressID
                              address {
                                id
                                streetAddress
                                unit
                                city
                                state
                                zip
                                zipPlusFour
                                longitude
                                latitude
                                createdAt
                                updatedAt
                              }
                              organizationID
                              createdAt
                              updatedAt
                              organization {
                                id
                                name
                                shortName
                                description
                                locationID
                                website
                                phoneNumber
                                stripeConnectedAccountID
                                stripeConnectedAccountStatus
                                headerImageURI
                                createdAt
                                updatedAt
                                owner
                              }
                            }
                            website
                            phoneNumber
                            stripeConnectedAccountID
                            stripeConnectedAccountStatus
                            headerImageURI
                            createdAt
                            updatedAt
                            owner
                          }
                        }
                      }
                    `,
                    variables: {
                        id: opportunities.selectedOpportunity.id
                    }
                })

                console.log('templating: ', getCurrentOpportunityDataResponse)

                if (getCurrentOpportunityDataResponse && getCurrentOpportunityDataResponse.data && getCurrentOpportunityDataResponse.data.getOpportunity) {
                    const { data }: any = await API.graphql({
                        query: createOpportunityTemplate,
                        variables: {
                            input: {
                                organizationID: authAccount.selectedStaffProfile.organizationID,
                                name: props.name,
                                description: props.description ? props.description : null,
                                templateJSON: JSON.stringify(getCurrentOpportunityDataResponse.data.getOpportunity)
                            }
                        }
                    })

                    console.log('created template response: ', data)

                    if (data && data.createOpportunityTemplate) {
                        dispatch(setLoading({ loading: false, message: 'Loading.' }))
                        toast.success('Template created.', {
                            position: "bottom-right",
                            autoClose: 4000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            containerId: 'main'
                        });
                        return {
                            opportunityTemplate: data.createOpportunityTemplate
                        }
                    }
                } else {
                    dispatch(setLoading({ loading: false, message: 'Loading.' }))
                }
            } else {
                dispatch(setLoading({ loading: false, message: 'Loading.' }))
            }


        } catch (err) {
            console.error(err)
            toast.error('Error creating template. Contact support.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });
            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        }
    }
)

export const templatesSlice = createSlice({
    name: 'templates',
    initialState,
    reducers: {
        clearLocalTemplateData: (state) => {
            state.organizationOpportunityTemplates = null
            state.flourishOpportunityTemplates = null
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getOrganizationOpportunityTemplates.fulfilled, (state, { payload }) => {
            if (payload && payload.opportunityTemplates) {
                state.organizationOpportunityTemplates = payload.opportunityTemplates
            }
        })
        builder.addCase(getFlourishOpportunityTemplates.fulfilled, (state, { payload }) => {
            if (payload && payload.opportunityTemplates) {
                state.flourishOpportunityTemplates = payload.opportunityTemplates
            }
        })
        builder.addCase(createOrganizationOpportunityTemplate.fulfilled, (state, { payload }) => {
            if (payload && payload.opportunityTemplate) {
                if (state.organizationOpportunityTemplates && state.organizationOpportunityTemplates.length > 0) {
                    state.organizationOpportunityTemplates.push(payload.opportunityTemplate)
                } else {
                    state.organizationOpportunityTemplates = [payload.opportunityTemplate]
                }
            }
        })
    }
})

export const { clearLocalTemplateData } = templatesSlice.actions
export default templatesSlice.reducer